/**
 * Кошелёк: баланс, пополнение, вывод и выписка.
 *
 * Пополнение — звёзды Telegram (нативный счёт через openInvoice) и CryptoBot.
 * Сумму зачисления определяет сервер, клиент только открывает оплату и ждёт
 * подтверждения.
 */

import { useEffect, useMemo, useState } from "react";

import { formatPaws, parsePaws, type PaymentMethodDto } from "@koshkodrop/shared";

import { ApiError, api } from "../lib/api";
import { haptic, openCryptoInvoice, openStarsInvoice } from "../lib/telegram";
import { useSession } from "../store/session";

const KIND_LABELS: Record<string, string> = {
  bet: "Ставка",
  payout: "Выигрыш",
  refund: "Возврат",
  deposit: "Пополнение",
  withdrawal: "Вывод",
  bonus: "Бонус",
  adjustment: "Корректировка",
};

const QUICK = ["100", "250", "500", "1000"];

interface Transaction {
  id: string;
  kind: string;
  amount: string;
  balanceAfter: string | null;
  createdAt: string;
}

interface Props {
  onOpenWithdraw?: () => void;
}

export function WalletPage({ onOpenWithdraw }: Props) {
  const balance = useSession((state) => state.balance);
  const heldBalance = useSession((state) => BigInt(state.user?.heldBalance ?? "0"));
  const refreshProfile = useSession((state) => state.refreshProfile);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [depositOpen, setDepositOpen] = useState(false);

  const reload = async () => {
    await refreshProfile();
    const data = await api.transactions();
    setTransactions(data.transactions);
  };

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await refreshProfile();
        const data = await api.transactions();
        if (!cancelled) setTransactions(data.transactions);
      } catch {
        // Пустая выписка честнее, чем устаревшая
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshProfile]);

  return (
    <div className="space-y-4">
      <section className="rounded-3xl border border-paws-500/20 bg-gradient-to-br from-paws-500/20 via-night-850 to-night-900 p-5 text-center">
        <p className="text-[11px] font-bold uppercase tracking-widest text-paws-400">Баланс</p>
        <p className="tabular mt-2 text-4xl font-extrabold text-white">
          {formatPaws(balance, { decimals: 2 })}
          <span className="ml-2 text-2xl">🐾</span>
        </p>
        <p className="mt-1 text-xs text-white/40">лапки — внутренняя валюта Кошкодропа</p>
        {heldBalance > 0n && (
          <p className="mt-2 text-xs font-semibold text-white/45">
            В холде: {formatPaws(heldBalance)} 🐾
          </p>
        )}
      </section>

      <div className="grid grid-cols-2 gap-2.5">
        <button
          type="button"
          onClick={() => {
            haptic.tap("medium");
            setDepositOpen(true);
          }}
          className="rounded-2xl bg-paws-500 py-4 text-base font-extrabold text-night-950 active:scale-[0.98]"
        >
          Пополнить
        </button>
        <button
          type="button"
          onClick={() => {
            haptic.tap("medium");
            onOpenWithdraw?.();
          }}
          className="rounded-2xl border border-paws-500/40 bg-paws-500/10 py-4 text-base font-extrabold text-paws-300 active:scale-[0.98]"
        >
          Вывод
        </button>
      </div>

      <section className="rounded-3xl border border-white/5 bg-night-850/60">
        <h3 className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white/40">
          История операций
        </h3>

        {loading ? (
          <p className="px-4 pb-4 text-sm text-white/35">Загружаю…</p>
        ) : transactions.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-white/35">Операций пока нет</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {transactions.map((item) => {
              const amount = BigInt(item.amount);
              const positive = amount > 0n;

              return (
                <li key={item.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white/80">
                      {KIND_LABELS[item.kind] ?? item.kind}
                    </p>
                    <p className="text-[11px] text-white/35">
                      {new Date(item.createdAt).toLocaleString("ru-RU", {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <span
                    className={`tabular text-sm font-bold ${
                      positive ? "text-win-400" : "text-white/60"
                    }`}
                  >
                    {positive ? "+" : ""}
                    {formatPaws(amount)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {depositOpen && (
        <DepositSheet
          onClose={() => setDepositOpen(false)}
          onPaid={async () => {
            await reload();
            setDepositOpen(false);
          }}
        />
      )}
    </div>
  );
}

function DepositSheet({ onClose, onPaid }: { onClose: () => void; onPaid: () => Promise<void> }) {
  const [methods, setMethods] = useState<PaymentMethodDto[]>([]);
  const [minPaws, setMinPaws] = useState(0n);
  const [maxPaws, setMaxPaws] = useState(0n);
  const [provider, setProvider] = useState<string | null>(null);
  const [amountText, setAmountText] = useState("100");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    void api
      .paymentMethods()
      .then((data) => {
        setMethods(data.methods);
        setMinPaws(BigInt(data.minAmountPaws));
        setMaxPaws(BigInt(data.maxAmountPaws));
        setProvider(data.methods[0]?.id ?? null);
      })
      .catch((caught) => {
        setError(caught instanceof ApiError ? caught.message : "Не удалось загрузить способы");
      });
  }, []);

  const amountPaws = useMemo(() => {
    try {
      return parsePaws(amountText.replace(",", ".").trim() || "0");
    } catch {
      return null;
    }
  }, [amountText]);

  const selected = methods.find((method) => method.id === provider) ?? null;
  const priceLabel = selected && amountPaws && amountPaws > 0n ? priceFor(selected, amountPaws, minPaws) : null;

  const pay = async () => {
    if (!selected || !amountPaws || amountPaws <= 0n || busy) return;
    if (amountPaws < minPaws || amountPaws > maxPaws) {
      haptic.error();
      setError(
        `Сумма от ${formatPaws(minPaws)} до ${formatPaws(maxPaws)} лапок`,
      );
      return;
    }

    setBusy(true);
    setError(null);
    setHint(null);
    try {
      const created = await api.createDeposit(selected.id, amountPaws.toString());
      const url = created.instructions.payUrl;
      if (!url) throw new Error("Провайдер не вернул ссылку на оплату");

      if (selected.id === "stars") {
        const status = await openStarsInvoice(url);
        if (status === "paid") {
          await waitUntilConfirmed(created.depositId);
          haptic.success();
          await onPaid();
          return;
        }
        if (status === "cancelled") {
          setHint("Оплату отменили");
        } else {
          setHint("Если счёт оплачен, лапки появятся через пару секунд");
          void waitUntilConfirmed(created.depositId)
            .then(async () => {
              haptic.success();
              await onPaid();
            })
            .catch(() => undefined);
        }
        return;
      }

      openCryptoInvoice(url);
      setHint("Оплатите счёт в CryptoBot — лапки зачислятся сами");
      void waitUntilConfirmed(created.depositId, 90)
        .then(async () => {
          haptic.success();
          await onPaid();
        })
        .catch(() => {
          setHint("Ещё не пришло. Можно закрыть окно: после оплаты баланс обновится сам.");
        });
    } catch (caught) {
      haptic.error();
      setError(caught instanceof ApiError ? caught.message : "Не удалось создать счёт");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-end bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-float-up safe-bottom w-full space-y-4 rounded-3xl border border-white/10 bg-night-850 p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-extrabold text-white">Пополнение</h3>
          <button type="button" onClick={onClose} className="text-sm text-white/40">
            закрыть
          </button>
        </div>

        {methods.length === 0 && !error ? (
          <p className="text-sm text-white/40">Загружаю способы…</p>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            {methods.map((method) => {
              const active = method.id === provider;
              return (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => {
                    haptic.select();
                    setProvider(method.id);
                  }}
                  className={`rounded-2xl border p-3 text-left ${
                    active
                      ? "border-paws-500/60 bg-paws-500/15"
                      : "border-white/8 bg-night-900"
                  }`}
                >
                  <p className="text-2xl leading-none">{method.id === "stars" ? "⭐" : "💎"}</p>
                  <p className="mt-2 text-sm font-extrabold text-white">
                    {method.id === "stars" ? "Stars" : "CryptoBot"}
                  </p>
                  <p className="mt-0.5 text-[11px] text-white/40">
                    {method.id === "stars" ? "1 ⭐ = 1 🐾" : "крипта через @CryptoBot"}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-2 rounded-2xl bg-night-900 px-3 py-2.5">
          <input
            value={amountText}
            onChange={(event) => setAmountText(event.target.value.replace(/[^\d.,]/g, ""))}
            inputMode="decimal"
            placeholder="Сумма"
            className="tabular w-full bg-transparent text-lg font-bold text-white outline-none placeholder:text-white/25"
          />
          <span className="text-sm font-semibold text-paws-400">🐾</span>
        </div>

        <div className="flex gap-2">
          {QUICK.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                haptic.select();
                setAmountText(value);
              }}
              className={`flex-1 rounded-xl py-2 text-sm font-bold ${
                amountText === value ? "bg-paws-500 text-night-950" : "bg-night-900 text-white/55"
              }`}
            >
              {value}
            </button>
          ))}
        </div>

        {priceLabel && (
          <p className="text-center text-sm font-bold text-white/70">
            К оплате: <span className="text-paws-300">{priceLabel}</span>
          </p>
        )}

        {error && <p className="text-center text-sm font-semibold text-lose-400">{error}</p>}
        {hint && !error && <p className="text-center text-xs font-semibold text-white/45">{hint}</p>}

        <button
          type="button"
          onClick={() => void pay()}
          disabled={busy || !selected || !amountPaws}
          className="w-full rounded-2xl bg-paws-500 py-4 text-base font-extrabold text-night-950 active:scale-[0.98] disabled:opacity-35"
        >
          {busy ? "Открываю счёт…" : "Оплатить"}
        </button>
      </div>
    </div>
  );
}

function priceFor(method: PaymentMethodDto, amountPaws: bigint, minPaws: bigint): string {
  const minExternal = Number(method.quoteForMin.amount);
  const min = Number(minPaws);
  if (!Number.isFinite(minExternal) || min <= 0) return "";

  const paid = (Number(amountPaws) * minExternal) / min;
  if (method.currency === "XTR") return `${Math.ceil(paid - 1e-9)} ⭐`;
  return `${paid.toFixed(2)} $`;
}

async function waitUntilConfirmed(depositId: string, seconds = 25): Promise<void> {
  const deadline = Date.now() + seconds * 1000;
  while (Date.now() < deadline) {
    const { deposit } = await api.getDeposit(depositId);
    if (deposit.status === "confirmed") return;
    if (deposit.status === "failed") throw new Error("Платёж не прошёл");
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
  throw new Error("timeout");
}
